import { uploadData } from '@app/apiExternalStorage/routes'
import { getPhotoUploadLinks } from '@app/api/routes/monitoringTasks'
import { isNullable, processLargeDataChunks, retryApiRequest, uploadFilesFactory } from '@app/utils'
import { retryS3Upload } from '@app/utils/retryS3Upload'
import { getPhotoUploadLinkInfoMapFromResponse } from '@app/utils/photos/getPhotoUploadLinkInfoMapFromResponse'

import type { FileInfoMap, UploadFilesFn } from '@app/types'
import type { PhotoUploadLinkInfoDto, PhotoUploadLinkInfoMapDto } from '@app/api/routes/monitoringTasks/dtos'

const getPhotosMapByName = (files: File[]): FileInfoMap => {
  return files.reduce((photosMap, file) => {
    ;(photosMap as FileInfoMap)[file.name] = file
    return photosMap
  }, {})
}

const appendHeadersToForm = (formData: FormData, headers: Record<string, string>): void => {
  Object.entries(headers).forEach(([headerKey, headerValue]) => {
    formData.append(headerKey, headerValue)
  })
}

const uploadPhotoToExternalStorage = async (
  photo: File,
  photoUploadLinkInfo: PhotoUploadLinkInfoDto,
  onPhotoLoaded?: (photo: File) => void
): Promise<void> => {
  const { url, fields } = photoUploadLinkInfo

  const photoFormData = new FormData()

  appendHeadersToForm(photoFormData, fields)

  photoFormData.append('file', photo, photo.name)

  await retryS3Upload(uploadData, 3)({ url, data: photoFormData })
  onPhotoLoaded?.(photo)
}

const uploadPhotosToExternalStorage = async (
  photosInfoMap: FileInfoMap,
  photoUploadLinkInfoMap: PhotoUploadLinkInfoMapDto | null,
  onPhotoLoaded?: (photo: File) => void
): Promise<void> => {
  if (isNullable(photoUploadLinkInfoMap)) return

  const uploadErrors: Array<{ fileName: string, error: Error }> = []

  for (const [fileName, photo] of Object.entries(photosInfoMap)) {
    const photoUploadLinkInfo: PhotoUploadLinkInfoDto | undefined = photoUploadLinkInfoMap[fileName]

    if (photoUploadLinkInfo) {
      try {
        await uploadPhotoToExternalStorage(photo, photoUploadLinkInfo, onPhotoLoaded)
      } catch (error) {
        uploadErrors.push({ fileName, error: error as Error })
      }
    }
  }

  if (uploadErrors.length > 0) {
    const errorMessage = uploadErrors.map(({ fileName, error }) => `${fileName}: ${error.message}`).join('; ')

    throw new Error(`Ошибки загрузки файлов: ${errorMessage}`)
  }
}

const getChunkedPhotoUploadLinks = processLargeDataChunks({
  queryFn: retryApiRequest(getPhotoUploadLinks, 1),
  dataPath: ['data', 'filenames'],
  chunkSize: 100
})

const uploadPhotos =
  (onPhotoUploaded?: (photo: File) => void) =>
    async (id: number, photos: File[]): Promise<void> => {
      const photosNameList = photos.map(photos => photos.name)
      const photosMapByName = getPhotosMapByName(photos)
      const photoUploadLinksResponse = await getChunkedPhotoUploadLinks({ id, data: { filenames: photosNameList } })
      const photoUploadLinkInfoMap = getPhotoUploadLinkInfoMapFromResponse(photoUploadLinksResponse)

      await uploadPhotosToExternalStorage(photosMapByName, photoUploadLinkInfoMap, onPhotoUploaded)
    }

export const handlePhotosUploadFactory = ({
  id,
  onPhotoUploaded
}: {
  id: string | number
  onPhotoUploaded?: (photo: File) => void
}): UploadFilesFn => {
  return uploadFilesFactory({ id, callback: uploadPhotos(onPhotoUploaded) })
}
